import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntdApp, Spin } from 'antd';
import koKR from 'antd/locale/ko_KR';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

// 개발용 자동 로그인 (임시)
import { enableDevAutoLogin } from './utils/devAuth';

// Styles
import './styles/globals.css';
import './styles/transitions.css';

// Components
import PrivateRoute from './components/PrivateRoute';
import MainLayout from './components/layout/MainLayout';

// Critical pages (loaded immediately)
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';

// Lazy-loaded pages for better performance
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const LeavesPage = React.lazy(() => import('./pages/LeavesPage'));
const LeaveDetailPage = React.lazy(() => import('./pages/LeaveDetailPage'));
const ExpensesPage = React.lazy(() => import('./pages/ExpensesPage'));
const ExpenseDetailPage = React.lazy(() => import('./pages/ExpenseDetailPage'));
const UsersPage = React.lazy(() => import('./pages/UsersPage'));
const UserDetailPage = React.lazy(() => import('./pages/UserDetailPage'));
const UserCreatePage = React.lazy(() => import('./pages/UserCreatePage'));
const TasksPage = React.lazy(() => import('./pages/TasksPage'));
const BooksPage = React.lazy(() => import('./pages/BooksPage'));
const SalesPage = React.lazy(() => import('./pages/SalesPage'));
const WikiPage = React.lazy(() => import('./pages/WikiPage'));
const OnlineMarketingPage = React.lazy(() => import('./pages/OnlineMarketingPage'));
const OfflineMarketingPage = React.lazy(() => import('./pages/OfflineMarketingPage'));
const DataManagerPage = React.lazy(() => import('./pages/DataManagerClean'));
const PartnersPage = React.lazy(() => import('./pages/PartnersPage'));
const BudgetsPage = React.lazy(() => import('./pages/BudgetsPage'));

// Set dayjs locale
dayjs.locale('ko');

// Create a client with optimized settings for small team collaboration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - 협업 환경에서 적절한 신선도
      gcTime: 1000 * 60 * 30, // 30 minutes - 더 긴 가비지 컬렉션
      refetchOnWindowFocus: false, // 창 포커스 시 자동 리페치 비활성화
      refetchOnReconnect: 'always', // 재연결 시 항상 리페치
      retry: 1, // 재시도 횟수 줄이기
    },
  },
});

function App() {
  // 개발용 자동 로그인 활성화 (임시)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      enableDevAutoLogin();
    }
  }, []);

  // 자주 사용하는 페이지들을 미리 로드 (3초 후)
  useEffect(() => {
    const timer = setTimeout(() => {
      // 자주 사용하는 페이지들을 백그라운드에서 미리 로드
      import('./pages/UsersPage');
      import('./pages/BooksPage');
      import('./pages/SalesPage');
      import('./pages/LeavesPage');
      import('./pages/ExpensesPage');
      import('./pages/TasksPage');
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        locale={koKR}
        theme={{
          token: {
            colorPrimary: '#7DD3C0',
            borderRadius: 8,
          },
        }}
      >
        <AntdApp>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Private Routes - All authenticated users can access all pages, backend enforces permissions */}
              <Route element={<PrivateRoute />}>
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={
                    <Suspense fallback={
                      <div className="flex justify-center items-center h-screen">
                        <div className="text-center">
                          <Spin size="large" />
                          <p className="mt-4 text-gray-500">대시보드 로딩 중...</p>
                        </div>
                      </div>
                    }>
                      <DashboardPage />
                    </Suspense>
                  } />
                  <Route path="/leaves" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><div className="text-center"><Spin size="large" /><p className="mt-2 text-gray-500">휴가 관리 로딩 중...</p></div></div>}>
                      <LeavesPage />
                    </Suspense>
                  } />
                  <Route path="/leaves/:id" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <LeaveDetailPage />
                    </Suspense>
                  } />
                  <Route path="/expenses" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><div className="text-center"><Spin size="large" /><p className="mt-2 text-gray-500">지출품의 로딩 중...</p></div></div>}>
                      <ExpensesPage />
                    </Suspense>
                  } />
                  <Route path="/expenses/:id" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <ExpenseDetailPage />
                    </Suspense>
                  } />
                  <Route path="/tasks" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><div className="text-center"><Spin size="large" /><p className="mt-2 text-gray-500">업무 관리 로딩 중...</p></div></div>}>
                      <TasksPage />
                    </Suspense>
                  } />
                  <Route path="/books" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><div className="text-center"><Spin size="large" /><p className="mt-2 text-gray-500">도서 관리 로딩 중...</p></div></div>}>
                      <BooksPage />
                    </Suspense>
                  } />
                  <Route path="/wiki" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <WikiPage />
                    </Suspense>
                  } />
                  <Route path="/online-marketing" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <OnlineMarketingPage />
                    </Suspense>
                  } />
                  <Route path="/offline-marketing" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <OfflineMarketingPage />
                    </Suspense>
                  } />
                  <Route path="/budgets" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <BudgetsPage />
                    </Suspense>
                  } />
                  <Route path="/partners" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <PartnersPage />
                    </Suspense>
                  } />
                  <Route path="/sales" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><div className="text-center"><Spin size="large" /><p className="mt-2 text-gray-500">판매 관리 로딩 중...</p></div></div>}>
                      <SalesPage />
                    </Suspense>
                  } />
                  <Route path="/data-manager" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <DataManagerPage />
                    </Suspense>
                  } />
                  <Route path="/users" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><div className="text-center"><Spin size="large" /><p className="mt-2 text-gray-500">사용자 관리 로딩 중...</p></div></div>}>
                      <UsersPage />
                    </Suspense>
                  } />
                  <Route path="/users/new" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <UserCreatePage />
                    </Suspense>
                  } />
                  <Route path="/users/:id" element={
                    <Suspense fallback={<div className="flex justify-center items-center h-64"><Spin size="large" /></div>}>
                      <UserDetailPage />
                    </Suspense>
                  } />
                </Route>
              </Route>
              
              {/* Fallback */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </AntdApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
